import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  CaretDown,
  CaretLeft,
  CaretRight,
  Columns,
  DotsThreeVertical,
  FileXls,
  FunnelSimple,
  Info,
  WarningCircle,
} from 'phosphor-react';

import { GlassCard, GlassPageBg } from '../../../../../components/glass';
import { useAuditoriaInteligente } from '../../../../../hooks/useAuditoriaInteligente';
import { AbaRazao, POR_PAGINA as POR_PAGINA_RAZAO } from './AbaRazao';
import {
  COLUNAS_PADRAO,
  alternar,
  dicaDaColuna,
  ehFixa,
  geometria,
  gravarColunas,
  lerColunas,
  normalizar,
  paramColunas,
} from './colunasVisiveis';
import { IColunaPapel, COLUNAS_COMPOSICAO } from './colunas';
import { FiltrosComposicao } from './FiltrosComposicao';
import {
  FILTROS_VAZIOS,
  IComposicaoItem,
  IComposicaoSaldo,
  IFiltrosUI,
  ITENS_POR_PAGINA_PADRAO,
  ItensPorPagina,
  OPCOES_ITENS_POR_PAGINA,
  contaFiltrosAtivos,
  filtrosDaUrl,
  filtrosParaParams,
  fmtCnpj,
  fmtData,
  fmtDias,
  fmtMoney,
  fmtSaldoComNatureza,
  itensEmAberto,
  itensCompensacao,
  itensNaoIdentificadosReais,
  normalizarItensPorPagina,
  ouTraco,
  periodoDoAno,
  seloConciliacao,
  valorComSinal,
  ILancamentoLigado,
  IPosicaoRazao,
  ILancamentosLigados,
} from './papelTrabalho';
import DrawerLigacoes from './DrawerLigacoes';
import { qtdLigacoes, temLigacao } from './ligacoes';

/** Preferências que sobrevivem entre visitas (baratas, não críticas). */
const PREF_TAMANHO = 'composicao:itens-por-pagina';
const PREF_FILTROS_ABERTOS = 'composicao:filtros-abertos';

/** Lê preferência do localStorage tolerando ambiente sem storage (SSR/privado). */
const lerPref = (chave: string): string | null => {
  try {
    return window.localStorage.getItem(chave);
  } catch {
    return null;
  }
};

const gravarPref = (chave: string, valor: string): void => {
  try {
    window.localStorage.setItem(chave, valor);
  } catch {
    /* preferência é conveniência: falhar ao gravar não pode quebrar a tela */
  }
};

const COR = {
  ink: '#0F172A',
  muted: '#64748B',
  faint: '#94A3B8',
  brand: '#1B88F5',
  pos: '#16A34A',
  warn: '#D97706',
  warnBg: 'rgba(217, 119, 6, 0.08)',
  linha: 'rgba(15, 23, 42, 0.08)',
} as const;

const BALANCETE = '/modulesHome/accountingConciliation/balancete';

/** Abas da tela. "Composição do saldo" é a ABA — o título é o papel de trabalho. */
type Aba = 'composicao' | 'movimentacao';

/** Tabular numbers: alinha as colunas de valor sem depender de fonte mono. */
const NUM = { fontVariantNumeric: 'tabular-nums' } as const;

export function PapelTrabalhoContaPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const {
    getComposicaoSaldo,
    getLancamentosPorNumero,
    getPosicaoNoRazao,
    baixarComposicaoSaldoXlsx,
  } = useAuditoriaInteligente();

  const [aba, setAba] = useState<Aba>('composicao');
  const queryClient = useQueryClient();
  const [baixando, setBaixando] = useState(false);
  const [erroDownload, setErroDownload] = useState<string | null>(null);

  // Dois estados de filtro de propósito: `rascunho` é o que o usuário digita,
  // `aplicados` é o que foi de fato enviado ao backend. Cada aplicação recompõe
  // o saldo de uma conta que pode ter 22 mil itens — filtrar a cada tecla
  // dispararia uma requisição pesada por caractere.
  // A tela pode ser aberta por link já filtrada (`?historico=juros`). Nesse
  // caso o recorte JÁ está valendo na primeira consulta, então rascunho e
  // aplicados nascem juntos — senão a grade viria cortada com os campos em
  // branco, que é exatamente o "300 de 22.095 sem dizer por quê".
  const filtrosIniciais = useMemo(() => filtrosDaUrl(params), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [rascunho, setRascunho] = useState<IFiltrosUI>(filtrosIniciais);
  const [aplicados, setAplicados] = useState<IFiltrosUI>(filtrosIniciais);
  const [offset, setOffset] = useState(0);

  /**
   * Nível de amostragem da grade (25/50/100). Padrão 25; a preferência do
   * usuário volta na próxima visita. Valor gravado é sempre normalizado — um
   * localStorage adulterado não pode virar `limit` inválido (422 no backend).
   */
  const [porPagina, setPorPagina] = useState<ItensPorPagina>(() =>
    lerPref(PREF_TAMANHO)
      ? normalizarItensPorPagina(lerPref(PREF_TAMANHO))
      : ITENS_POR_PAGINA_PADRAO,
  );

  /**
   * Bloco de filtros recolhível. Começa RECOLHIDO — a grade é o que importa ao
   * abrir a conta —, com duas exceções:
   *
   *  1. FILTRO VINDO DA URL abre expandido SEMPRE, acima de qualquer
   *     preferência: o recorte já está valendo, e escondê-lo faria a grade
   *     mostrar um subconjunto sem explicação.
   *  2. Na ausência de filtro herdado, vale a preferência salva da última
   *     visita.
   */
  // O título cujo painel de pagamentos está aberto. `null` = fechado.
  const [itemLigacoes, setItemLigacoes] = useState<IComposicaoItem | null>(null);
  // O lançamento a destacar na aba Razão, e em que offset ele cai.
  const [foco, setFoco] = useState<{ numero: number; offset: number } | null>(null);
  const [filtrosAbertos, setFiltrosAbertos] = useState(
    () => contaFiltrosAtivos(filtrosIniciais) > 0 || lerPref(PREF_FILTROS_ABERTOS) === 'true',
  );

  // Contrato definido pelo Balancete (#709) em `papelDeTrabalhoUrl`:
  // ?empresa=<id interno>&conta=<classificacao>&codigo=<codigo_conta>
  // &data_ini=YYYY-MM-DD&data_fim=YYYY-MM-DD (data-posição do saldo).
  const empresaId = Number(params.get('empresa'));
  const classificacao = params.get('conta') ?? '';
  // O endpoint recorta por `extract_plano_contas.codigo` — é o `codigo`, não a
  // classificação. Sem ele não há o que consultar.
  const codigo = params.get('codigo') ?? '';
  const dataIni = params.get('data_ini') ?? '';
  const dataFim = params.get('data_fim') ?? '';

  const paramsOk =
    Number.isFinite(empresaId) && empresaId > 0 && !!codigo && !!dataIni && !!dataFim;

  // Os filtros aplicados entram na queryKey: trocar filtro ou página é uma
  // consulta nova (e o cache do TanStack guarda cada página separadamente).

  /**
   * Os pagamentos do título, buscados PELO NÚMERO.
   *
   * Antes isto vinha da página carregada do razão, e escondia o que não
   * coubesse: medido na NF 22401, 9 de 13 pagamentos sumiam — e nenhum era de
   * outro exercício, era paginação (a conta tem 4.435 lançamentos e a página
   * traz 200). Buscar pelo número não depende de página nenhuma.
   *
   * `enabled` só quando o painel abre: a maioria dos acessos nem o abre.
   */
  const numerosLigados = (itemLigacoes?.ligacoes ?? []).join(',');
  const { data: ligados, isFetching: carregandoLigados } = useQuery<ILancamentosLigados>({
    queryKey: [
      'lancamentos-ligados',
      {
        id: empresaId,
        numeros: numerosLigados,
        conta: codigo,
        data_ini: dataIni,
        data_fim: dataFim,
      },
    ],
    queryFn: getLancamentosPorNumero,
    enabled: !!numerosLigados && paramsOk,
    staleTime: 5 * 60 * 1000,
  });

  /**
   * Leva o usuário até a linha do pagamento na aba Razão.
   *
   * Pedido do usuário: "clicar no lançamento e ir direto para a linha dele".
   * O destino é o RAZÃO, e não a composição: a baixa é consumida pelo matching
   * e não aparece em `tb_composicao_saldo_item` — só a provisão fica.
   *
   * A página vem do backend (`/razao/posicao`), que ordena igual ao razão.
   * Calcular aqui exigiria repetir a ordenação no cliente, e duas réguas para
   * a mesma pergunta divergem.
   */
  const irAoRazao = async (l: ILancamentoLigado) => {
    if (l.numero == null) return;
    const pos = await queryClient.fetchQuery<IPosicaoRazao>({
      queryKey: [
        'razao-posicao',
        {
          id: empresaId,
          conta: codigo,
          numero: l.numero,
          por_pagina: POR_PAGINA_RAZAO,
          data_ini: dataIni,
          data_fim: dataFim,
        },
      ],
      queryFn: getPosicaoNoRazao,
      staleTime: 5 * 60 * 1000,
    });
    // `pos` nulo = o lançamento não pertence a esta conta no período. Levar o
    // usuário a uma página onde não há nada seria pior que não navegar; o
    // painel continua aberto com a informação na tela.
    if (pos?.offset == null) return;
    setFoco({ numero: l.numero, offset: pos.offset });
    setItemLigacoes(null);
    setAba('movimentacao');
  };
  const paramsFiltro = useMemo(() => filtrosParaParams(aplicados), [aplicados]);

  const { data, isFetching, isError, error } = useQuery<IComposicaoSaldo>({
    queryKey: [
      'composicao-saldo',
      {
        id: empresaId,
        conta: codigo,
        data_ini: dataIni,
        data_fim: dataFim,
        data_posicao: dataFim,
        limit: porPagina,
        offset,
        ...paramsFiltro,
      },
    ],
    queryFn: getComposicaoSaldo,
    enabled: paramsOk,
    // Mantém a página anterior visível enquanto a próxima carrega — sem isso a
    // grade pisca vazia a cada clique de paginação.
    placeholderData: anterior => anterior,
  });

  const paginacao = data?.paginacao;
  const resumoFiltro = data?.filtro;

  /** Aplica o rascunho e volta para a primeira página (senão o usuário
   *  filtraria e cairia numa página que o novo recorte talvez nem tenha). */
  const aplicarFiltros = () => {
    setAplicados(rascunho);
    setOffset(0);
  };

  const limparFiltros = () => {
    setRascunho(FILTROS_VAZIOS);
    setAplicados(FILTROS_VAZIOS);
    setOffset(0);
  };

  const irParaPagina = (novaPagina: number) => {
    const total = paginacao?.paginas ?? 1;
    const alvo = Math.min(Math.max(1, novaPagina), Math.max(1, total));
    // O offset acompanha o TAMANHO ESCOLHIDO. Com a constante fixa de 100, a
    // página 2 em amostragem de 25 saltava para o item 101 em vez do 26.
    setOffset((alvo - 1) * porPagina);
  };

  /**
   * Troca o nível de amostragem e VOLTA PARA A PÁGINA 1: o offset atual foi
   * calculado com o tamanho antigo e, mantido, apontaria para outro lugar da
   * lista (offset 75 em páginas de 25 é a página 4; em páginas de 100 nem
   * existe).
   */
  const trocarPorPagina = (valor: string) => {
    const novo = normalizarItensPorPagina(valor);
    setPorPagina(novo);
    setOffset(0);
    gravarPref(PREF_TAMANHO, String(novo));
  };

  const alternarFiltros = () => {
    setFiltrosAbertos(atual => {
      gravarPref(PREF_FILTROS_ABERTOS, String(!atual));
      return !atual;
    });
  };

  const emAberto = useMemo(() => itensEmAberto(data?.itens ?? []), [data]);
  // As órfãs se dividem em dois blocos. A compensação de adiantamento TEM
  // explicação contábil; misturá-la com o "não identificado" manda o contador
  // conferir à mão linhas que já estão certas. Nenhum total muda: o backend
  // não alterou tipo nem valor desses itens, só o rótulo.
  const naoIdentificados = useMemo(() => itensNaoIdentificadosReais(data?.itens ?? []), [data]);
  const compensacoes = useMemo(() => itensCompensacao(data?.itens ?? []), [data]);

  // O selo do rodapé. Sem liquidação na base, `conciliado` vem `true` sem
  // significar nada — ver `seloConciliacao`.
  // COLUNAS VISÍVEIS — escolha do usuário, por navegador. Toda a geometria da
  // tabela (colSpan do rodapé, largura mínima) sai daqui: esconder uma coluna
  // sem recalcular faria o total somar debaixo da coluna errada.
  const [colunasVisiveis, setColunasVisiveis] = useState<string[]>(() => lerColunas());
  const geo = useMemo(() => geometria(colunasVisiveis), [colunasVisiveis]);

  const alternarColuna = (titulo: string) =>
    setColunasVisiveis(prev => {
      const proximo = alternar(prev, titulo);
      gravarColunas(proximo);
      return proximo;
    });

  const selo = useMemo(() => seloConciliacao(Boolean(data?.conciliado), data?.liquidacao), [data]);

  /** Estado do corpo da tabela — evita ternário aninhado no JSX. */
  const estadoTabela: 'carregando' | 'vazio' | 'itens' = (() => {
    if (isFetching && !data) return 'carregando';
    if (emAberto.length === 0 && naoIdentificados.length === 0 && compensacoes.length === 0)
      return 'vazio';
    return 'itens';
  })();

  const nomeConta = data?.conta?.nome ?? '';
  // O GRUPO a que esta conta pertence ("Bancos Conta Movimento" acima de
  // "Banco do Brasil S/A 111621-5."). Num plano de 5 níveis, o nome da
  // analítica sozinho não diz onde o contador está. `null` (conta de 1º nível
  // ou plano sem sintéticas) simplesmente omite a linha — nunca inventa grupo.
  const sinteticaPai = data?.conta?.sintetica_pai ?? null;
  const codigoExibido = data?.conta?.codigo ?? codigo;
  const ano = data?.ano;
  const anoAnterior = data?.ano_anterior;

  /** Link "ver <ano-1> ›": mesma conta, exercício anterior, mesma rota. */
  const verAnoAnterior = () => {
    if (!anoAnterior) return;
    const p = periodoDoAno(anoAnterior);
    const novo = new URLSearchParams(params);
    novo.set('data_ini', p.data_ini);
    novo.set('data_fim', p.data_fim);
    setParams(novo);
  };

  /**
   * Baixa o papel de trabalho em XLSX. O arquivo é montado no backend a partir
   * do MESMO motor de composição que alimenta esta tela — aqui só disparamos o
   * download e cuidamos do estado visual.
   */
  const baixarExcel = async (escopo: 'completo' | 'filtrado') => {
    if (!paramsOk || baixando) return;
    setBaixando(true);
    setErroDownload(null);
    try {
      await baixarComposicaoSaldoXlsx({
        id: empresaId,
        conta: codigo,
        data_ini: dataIni,
        data_fim: dataFim,
        data_posicao: dataFim,
        escopo,
        // As colunas VISÍVEIS viram as colunas da aba "Conta analítica": o
        // arquivo é a visualização que está na tela (pedido do usuário).
        colunas: paramColunas(colunasVisiveis),
        // Só mandamos filtros no escopo filtrado: "conta inteira" tem que
        // significar conta inteira, mesmo com filtro ativo na tela.
        ...(escopo === 'filtrado' ? paramsFiltro : {}),
      });
    } catch {
      setErroDownload('Não foi possível gerar o Excel desta conta. Tente novamente.');
    } finally {
      setBaixando(false);
    }
  };

  const voltarAoBalancete = () => {
    const p = new URLSearchParams();
    if (empresaId) p.set('empresa', String(empresaId));
    navigate(`${BALANCETE}?${p.toString()}`);
  };

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        {/* ── Faixa de topo: [← Composição de Saldo]  Papel de trabalho da conta ── */}
        <Flex align="center" gap={4} mb={5} wrap="wrap">
          <Button
            size="sm"
            borderRadius="lg"
            bg={COR.brand}
            color="white"
            fontWeight="700"
            leftIcon={<ArrowLeft size={16} weight="bold" />}
            _hover={{ bg: '#1573D1' }}
            onClick={voltarAoBalancete}
          >
            Composição de Saldo
          </Button>
          <Text fontSize={{ base: '18px', md: '20px' }} fontWeight="700" color={COR.ink}>
            Papel de trabalho da conta
          </Text>
        </Flex>

        {!paramsOk ? (
          <GlassCard intensity="medium" py={16}>
            <Flex direction="column" align="center" gap={3}>
              <WarningCircle size={40} weight="duotone" color="#CBD5E1" />
              <Text fontSize="sm" color={COR.faint} textAlign="center" maxW="420px">
                Papel de trabalho aberto sem conta definida. Volte à Composição de Saldo e clique
                numa conta analítica.
              </Text>
              <Button size="sm" variant="outline" onClick={voltarAoBalancete}>
                Ir para a Composição de Saldo
              </Button>
            </Flex>
          </GlassCard>
        ) : (
          <GlassCard intensity="medium" p={{ base: 4, md: 5 }}>
            {/* ── Conta SINTÉTICA a que esta analítica pertence ──
                Fica ACIMA do nome da conta, em letra menor: num plano de 5
                níveis, "Banco do Brasil S/A 111621-5." sozinho não diz em que
                grupo o contador está. Omitida quando não há pai (conta de 1º
                nível, ou plano da empresa sem sintéticas) — nunca inventamos
                um grupo. */}
            {sinteticaPai?.nome && (
              <Flex align="center" gap={1.5} mb={1.5} minW={0}>
                <Text
                  fontSize="11px"
                  fontWeight="700"
                  color={COR.muted}
                  letterSpacing="0.06em"
                  textTransform="uppercase"
                  flexShrink={0}
                >
                  Conta sintética
                </Text>
                <Text fontSize="12.5px" color={COR.faint} flexShrink={0}>
                  ·
                </Text>
                <Text
                  fontSize="12.5px"
                  color={COR.muted}
                  isTruncated
                  title={
                    sinteticaPai.classificacao
                      ? `${sinteticaPai.classificacao} ${sinteticaPai.nome}`
                      : sinteticaPai.nome
                  }
                >
                  {sinteticaPai.nome}
                </Text>
              </Flex>
            )}

            {/* ── Header: [186] FGTS a Pagar 2025  [abas]      R$ 656,00 C saldo 2025 ── */}
            <Flex align="center" gap={3} wrap="wrap" justify="space-between">
              <Flex align="center" gap={3} wrap="wrap" minW={0}>
                <Badge
                  borderRadius="md"
                  px={2}
                  py={1}
                  fontSize="12px"
                  fontWeight="700"
                  textTransform="none"
                  bg="rgba(15, 23, 42, 0.06)"
                  color={COR.muted}
                  title={classificacao ? `Classificação ${classificacao}` : undefined}
                >
                  {codigoExibido}
                </Badge>
                <Text fontSize="17px" fontWeight="700" color={COR.ink} isTruncated>
                  {nomeConta || '—'}
                </Text>
                {ano != null && (
                  <Text fontSize="17px" fontWeight="600" color={COR.muted}>
                    {ano}
                  </Text>
                )}

                {/* Abas: Composição do saldo | Razão */}
                <Flex
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={COR.brand}
                  overflow="hidden"
                  ml={{ base: 0, md: 2 }}
                >
                  {(
                    [
                      ['composicao', 'Conta analítica'],
                      ['movimentacao', 'Razão'],
                    ] as [Aba, string][]
                  ).map(([id, rotulo]) => (
                    <Box
                      key={id}
                      as="button"
                      px={4}
                      py={2}
                      fontSize="13px"
                      fontWeight="700"
                      bg={aba === id ? COR.brand : 'white'}
                      color={aba === id ? 'white' : COR.brand}
                      _hover={{ bg: aba === id ? '#1573D1' : 'rgba(27, 136, 245, 0.08)' }}
                      onClick={() => setAba(id)}
                      {...{ type: 'button' }}
                    >
                      {rotulo}
                    </Box>
                  ))}
                </Flex>
              </Flex>

              {/* Saldo com NATUREZA à direita — letra vinda do backend (ver papelTrabalho.ts). */}
              <Flex align="baseline" gap={1.5} flexShrink={0}>
                <Text fontSize="19px" fontWeight="700" color={COR.ink} sx={NUM}>
                  {fmtSaldoComNatureza(data?.saldo_razao, data?.saldo_razao_natureza)}
                </Text>
                {ano != null && (
                  <Text fontSize="12px" color={COR.muted}>
                    saldo {ano}
                  </Text>
                )}
              </Flex>
            </Flex>

            {isError && (
              <Flex
                mt={4}
                align="center"
                gap={2}
                bg={COR.warnBg}
                borderRadius="lg"
                px={3}
                py={2.5}
                border="1px solid rgba(217, 119, 6, 0.25)"
              >
                <WarningCircle size={16} weight="fill" color="#D97706" />
                <Text fontSize="13px" color={COR.warn}>
                  Não foi possível carregar a composição do saldo desta conta.
                  {error instanceof Error && error.message ? ` (${error.message})` : ''}
                </Text>
              </Flex>
            )}

            {aba === 'movimentacao' ? (
              <AbaRazao
                empresaId={empresaId}
                conta={codigo}
                dataIni={dataIni}
                dataFim={dataFim}
                // Só consulta quando a aba está aberta: a razão pode ter
                // dezenas de milhares de lançamentos e não faz sentido
                // carregá-la para quem ficou na composição.
                habilitado={paramsOk && aba === 'movimentacao'}
                // Destaque vindo do painel de pagamentos: leva à página e à
                // linha do lançamento clicado.
                focoNumero={foco?.numero ?? null}
                focoOffset={foco?.offset ?? null}
              />
            ) : (
              <>
                {/* ── Linha de contexto: data-base · razão · anterior · ver <ano-1> ›   [Baixar Excel] ── */}
                <Flex mt={4} align="center" gap={2} wrap="wrap" justify="space-between">
                  <Flex align="center" gap={1.5} wrap="wrap" fontSize="13px" minW={0}>
                    <Text fontWeight="700" color={COR.ink}>
                      Data-base:
                    </Text>
                    <Text color={COR.ink} sx={NUM}>
                      {fmtData(data?.data_posicao ?? dataFim)}
                    </Text>
                    <Text color={COR.faint}>·</Text>
                    <Text color={COR.muted}>saldo do razão:</Text>
                    <Text fontWeight="700" color={COR.ink} sx={NUM}>
                      {fmtSaldoComNatureza(data?.saldo_razao, data?.saldo_razao_natureza)}
                    </Text>
                    <Text color={COR.faint}>·</Text>
                    <Text color={COR.muted}>saldo anterior:</Text>
                    <Text fontWeight="700" color={COR.ink} sx={NUM}>
                      {fmtSaldoComNatureza(data?.saldo_anterior, data?.saldo_anterior_natureza)}
                    </Text>
                    {anoAnterior != null && (
                      <Box
                        as="button"
                        display="inline-flex"
                        alignItems="center"
                        gap={0.5}
                        color={COR.brand}
                        fontWeight="600"
                        textDecoration="underline"
                        _hover={{ color: '#1573D1' }}
                        onClick={verAnoAnterior}
                        {...{ type: 'button' }}
                      >
                        ver {anoAnterior}
                        <CaretRight size={12} weight="bold" />
                      </Box>
                    )}
                  </Flex>

                  {/* ── Colunas visíveis ──
                      Escolha do usuário, salva no navegador dele. Não muda
                      nenhum número: o total e a conciliação continuam da conta
                      inteira — é conveniência de leitura. O XLSX baixa
                      EXATAMENTE estas colunas na aba "Conta analítica" (pedido
                      do usuário: o arquivo é a visualização da tela). Duas
                      colunas não saem: "Data" ordena a tabela e "Em aberto" é
                      a que o total soma. */}
                  <Menu placement="bottom-end" closeOnSelect={false}>
                    <MenuButton
                      as={Button}
                      size="sm"
                      variant="ghost"
                      color={COR.muted}
                      fontWeight="600"
                      leftIcon={<Columns size={15} weight="bold" />}
                      rightIcon={<CaretDown size={12} weight="bold" />}
                      title="Escolher quais colunas aparecem (só para você)"
                    >
                      Colunas ({geo.total})
                    </MenuButton>
                    <MenuList maxH="340px" overflowY="auto" minW="240px">
                      {COLUNAS_COMPOSICAO.map(c => {
                        const marcada = colunasVisiveis.includes(c.titulo);
                        const fixa = ehFixa(c.titulo);
                        return (
                          <MenuItem
                            key={c.titulo}
                            fontSize="13px"
                            isDisabled={fixa}
                            onClick={() => alternarColuna(c.titulo)}
                            title={dicaDaColuna(c.titulo, marcada)}
                          >
                            <Flex align="center" gap={2} w="100%">
                              <Box
                                w="14px"
                                h="14px"
                                borderRadius="3px"
                                borderWidth="1.5px"
                                borderColor={marcada ? COR.brand : 'rgba(15,23,42,0.25)'}
                                bg={marcada ? COR.brand : 'transparent'}
                                flexShrink={0}
                              />
                              <Text flex={1}>{c.titulo}</Text>
                              {fixa && (
                                <Text fontSize="10px" color={COR.faint}>
                                  fixa
                                </Text>
                              )}
                            </Flex>
                          </MenuItem>
                        );
                      })}
                      <MenuItem
                        fontSize="12px"
                        color={COR.brand}
                        fontWeight="700"
                        borderTopWidth="1px"
                        borderColor={COR.linha}
                        onClick={() => {
                          const padrao = normalizar([...COLUNAS_PADRAO]);
                          setColunasVisiveis(padrao);
                          gravarColunas(padrao);
                        }}
                      >
                        Restaurar padrão
                      </MenuItem>
                    </MenuList>
                  </Menu>

                  {/* O XLSX é gerado no backend, a partir do mesmo motor desta
                      tela, com DUAS ABAS — "Conta analítica" e "Razão", as
                      mesmas da tela (pedido do usuário). A aba Conta analítica
                      leva as colunas VISÍVEIS aqui escolhidas: o arquivo é a
                      visualização que está na tela. Duas opções: a conta
                      INTEIRA (papel de trabalho completo) ou o recorte
                      filtrado — o arquivo filtrado se anuncia como recorte no
                      próprio cabeçalho e no nome; a aba Razão nunca é
                      filtrada (a tela também não filtra a razão). */}
                  <Menu placement="bottom-end">
                    <MenuButton
                      as={Button}
                      size="sm"
                      variant="link"
                      color={COR.brand}
                      fontWeight="600"
                      textDecoration="underline"
                      leftIcon={<FileXls size={15} weight="bold" />}
                      rightIcon={<CaretDown size={12} weight="bold" />}
                      isLoading={baixando}
                      loadingText="Gerando..."
                      isDisabled={!data || baixando}
                      title={
                        data
                          ? 'Baixar o papel de trabalho desta conta em Excel'
                          : 'Aguarde a composição carregar'
                      }
                      aria-label="Baixar Excel"
                    >
                      Baixar Excel
                    </MenuButton>
                    <MenuList fontSize="13px" minW="290px">
                      <MenuItem onClick={() => baixarExcel('completo')}>
                        <Box>
                          <Text fontWeight="700" color={COR.ink}>
                            Conta inteira
                          </Text>
                          <Text fontSize="11px" color={COR.muted}>
                            Abas Conta analítica ({geo.total} colunas, as da tela) e Razão
                            {paginacao?.total_itens != null
                              ? ` · ${paginacao.total_itens} itens`
                              : ''}
                          </Text>
                        </Box>
                      </MenuItem>
                      <MenuItem
                        isDisabled={!resumoFiltro?.ativo}
                        onClick={() => baixarExcel('filtrado')}
                      >
                        <Box>
                          <Text fontWeight="700" color={COR.ink}>
                            Apenas o filtro atual
                          </Text>
                          <Text fontSize="11px" color={COR.muted}>
                            {resumoFiltro?.ativo
                              ? `Recorte filtrado · ${resumoFiltro.quantidade} itens`
                              : 'Aplique um filtro para habilitar'}
                          </Text>
                        </Box>
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </Flex>

                {/* Falha na geração do Excel — não derruba a tela, só avisa. */}
                {erroDownload && (
                  <Flex
                    mt={3}
                    align="center"
                    gap={2}
                    bg={COR.warnBg}
                    borderRadius="lg"
                    px={3}
                    py={2.5}
                    border="1px solid rgba(217, 119, 6, 0.25)"
                  >
                    <WarningCircle size={16} weight="fill" color="#D97706" />
                    <Text fontSize="13px" color={COR.warn}>
                      {erroDownload}
                    </Text>
                  </Flex>
                )}

                {/* ── Aviso de carga sem liquidação ──
                    Vem ANTES do aviso de abertura e em âmbar, não em azul: os
                    outros avisos dizem "falta um dado"; este diz que o selo
                    "saldo conciliado" logo abaixo NÃO significa o que parece.
                    A conciliação fecha porque os dois lados leem a mesma base
                    incompleta — sem este aviso o contador dá a conta por
                    conferida. O texto vem pronto do backend: a explicação é
                    regra de negócio e duplicá-la aqui faria as duas versões
                    divergirem. */}
                {data?.liquidacao?.aviso && (
                  <Flex
                    mt={3}
                    align="flex-start"
                    gap={2}
                    bg={COR.warnBg}
                    border="1px solid rgba(217, 119, 6, 0.30)"
                    borderRadius="lg"
                    px={3}
                    py={2.5}
                  >
                    <Box pt="2px" flexShrink={0}>
                      <WarningCircle size={16} weight="fill" color={COR.warn} />
                    </Box>
                    <Text fontSize="12.5px" color={COR.muted} lineHeight="1.5">
                      <b>Atenção à conciliação desta empresa.</b> {data.liquidacao.aviso}
                    </Text>
                  </Flex>
                )}

                {/* ── Aviso de abertura indisponível — caminho NORMAL hoje, não erro ── */}
                {data && !data.abertura_disponivel && (
                  <Flex
                    mt={3}
                    align="flex-start"
                    gap={2}
                    bg="rgba(27, 136, 245, 0.06)"
                    border="1px solid rgba(27, 136, 245, 0.20)"
                    borderRadius="lg"
                    px={3}
                    py={2.5}
                  >
                    <Box pt="2px" flexShrink={0}>
                      <Info size={16} weight="fill" color={COR.brand} />
                    </Box>
                    <Text fontSize="12.5px" color={COR.muted} lineHeight="1.5">
                      <b>Saldo de abertura não disponível na base</b> para esta conta. A composição
                      abaixo cobre apenas o movimento do período, então ela pode não fechar com o
                      saldo do balancete oficial. A informação manual da abertura chega numa próxima
                      entrega.
                    </Text>
                  </Flex>
                )}

                {/* ── Filtros ── */}
                <FiltrosComposicao
                  rascunho={rascunho}
                  onChange={setRascunho}
                  onAplicar={aplicarFiltros}
                  onLimpar={limparFiltros}
                  aplicados={aplicados}
                  carregando={isFetching}
                  aberto={filtrosAbertos}
                  onAlternar={alternarFiltros}
                />

                {/* ── Soma do SUBCONJUNTO filtrado ──
                    Bloco separado e explicitamente rotulado. NÃO substitui o
                    TOTAL DA COMPOSIÇÃO (que é da conta inteira e aparece no pé
                    da tabela): um total que mudasse com o filtro faria a linha
                    de conciliação parecer divergência onde não há. */}
                {resumoFiltro?.ativo && (
                  <Flex
                    mt={3}
                    align="center"
                    gap={2}
                    wrap="wrap"
                    bg="rgba(27, 136, 245, 0.06)"
                    border="1px solid rgba(27, 136, 245, 0.20)"
                    borderRadius="lg"
                    px={3}
                    py={2.5}
                  >
                    <FunnelSimple size={14} weight="bold" color={COR.brand} />
                    <Text fontSize="12.5px" fontWeight="700" color={COR.brand}>
                      Soma do filtro:
                    </Text>
                    <Text fontSize="13px" fontWeight="700" color={COR.ink} sx={NUM}>
                      {fmtMoney(resumoFiltro.soma)}
                    </Text>
                    <Text color={COR.faint}>·</Text>
                    <Text fontSize="12.5px" color={COR.muted}>
                      {resumoFiltro.quantidade} {resumoFiltro.quantidade === 1 ? 'item' : 'itens'}{' '}
                      de {paginacao?.total_itens ?? 0}
                    </Text>
                    <Box flex="1" minW="8px" />
                    {/* Frase curta que impede a leitura errada do bloco. */}
                    <Text fontSize="11px" color={COR.muted} fontStyle="italic">
                      o total da composição abaixo continua sendo o da conta inteira
                    </Text>
                  </Flex>
                )}

                {/* ── Tabela da composição ── */}
                <Box mt={4} overflowX="auto">
                  {/* `fixed` + largura por coluna: no layout automático as
                      colunas curtas (Filial, Dias, CNPJ) roubavam espaço de
                      Fornecedor e Histórico, que truncavam em uma palavra. O
                      `minW` garante que em tela estreita a tabela role em vez
                      de espremer — o container já tem overflowX. */}
                  <Table
                    size="sm"
                    variant="unstyled"
                    sx={{ tableLayout: 'fixed' }}
                    minW={geo.larguraMinima}
                  >
                    <Thead>
                      <Tr borderBottom="1px solid" borderColor={COR.linha}>
                        {/* compensa a célula de ação das linhas (ver LinhaItem) */}
                        <Th px={1} width="34px" />
                        {geo.colunas.map(c => (
                          <Th
                            key={c.titulo}
                            fontSize="10px"
                            letterSpacing="0.08em"
                            color={COR.muted}
                            fontWeight="600"
                            py={2.5}
                            px={3}
                            width={c.largura}
                            textAlign={c.alinha ?? 'left'}
                            whiteSpace="nowrap"
                          >
                            {c.titulo}
                          </Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {estadoTabela === 'carregando' && (
                        <Tr>
                          <Td colSpan={geo.total + 1} py={10}>
                            <Flex justify="center" align="center" gap={2}>
                              <Spinner size="sm" color={COR.brand} />
                              <Text fontSize="sm" color={COR.faint}>
                                Compondo o saldo...
                              </Text>
                            </Flex>
                          </Td>
                        </Tr>
                      )}

                      {estadoTabela === 'vazio' && (
                        <Tr>
                          <Td colSpan={geo.total + 1} py={10}>
                            <Text fontSize="sm" color={COR.faint} textAlign="center">
                              Nenhum item em aberto nesta conta na data-base.
                            </Text>
                          </Td>
                        </Tr>
                      )}

                      {estadoTabela === 'itens' && (
                        <>
                          {emAberto.map(item => (
                            <LinhaItem
                              key={chaveItem(item)}
                              item={item}
                              colunas={geo.colunas}
                              onAbrirLigacoes={setItemLigacoes}
                            />
                          ))}

                          {/* Seção "compensação de adiantamento": órfãs COM
                              explicação contábil. Vem antes do "não
                              identificado" e em azul, não em âmbar — âmbar diz
                              "confira isto", e aqui não há o que conferir. */}
                          {compensacoes.length > 0 && (
                            <>
                              <Tr>
                                <Td colSpan={geo.total + 1} px={3} pt={4} pb={1.5}>
                                  <Flex align="center" gap={1.5} wrap="wrap">
                                    <Info size={13} weight="fill" color={COR.brand} />
                                    <Text
                                      fontSize="10px"
                                      fontWeight="700"
                                      color={COR.brand}
                                      letterSpacing="0.08em"
                                      textTransform="uppercase"
                                    >
                                      Compensação de adiantamento
                                    </Text>
                                    <Text fontSize="11px" color={COR.muted}>
                                      — o fornecedor foi pago antes da nota; este lançamento baixa o
                                      adiantamento. Entra no total, e não precisa de conferência.
                                    </Text>
                                  </Flex>
                                </Td>
                              </Tr>
                              {compensacoes.map(item => (
                                <LinhaItem
                                  key={chaveItem(item)}
                                  item={item}
                                  colunas={geo.colunas}
                                  onAbrirLigacoes={setItemLigacoes}
                                />
                              ))}
                            </>
                          )}

                          {/* Seção "não identificado": FIM da lista e SOMA no total. */}
                          {naoIdentificados.length > 0 && (
                            <>
                              <Tr>
                                <Td colSpan={geo.total + 1} px={3} pt={4} pb={1.5}>
                                  <Flex align="center" gap={1.5}>
                                    <WarningCircle size={13} weight="fill" color="#D97706" />
                                    <Text
                                      fontSize="10px"
                                      fontWeight="700"
                                      color={COR.warn}
                                      letterSpacing="0.08em"
                                      textTransform="uppercase"
                                    >
                                      Não identificado
                                    </Text>
                                    <Text fontSize="11px" color={COR.muted}>
                                      — baixa sem provisão correspondente. Entra no total; não foi
                                      reclassificada.
                                    </Text>
                                  </Flex>
                                </Td>
                              </Tr>
                              {naoIdentificados.map(item => (
                                <LinhaItem
                                  key={chaveItem(item)}
                                  item={item}
                                  destaque
                                  colunas={geo.colunas}
                                  onAbrirLigacoes={setItemLigacoes}
                                />
                              ))}
                            </>
                          )}

                          {/* TOTAL DA COMPOSIÇÃO */}
                          <Tr borderTop="2px solid" borderColor="rgba(15, 23, 42, 0.15)">
                            {/* +1 pela célula de ação, que não está em `colunas` */}
                            <Td colSpan={geo.rotulo + 1} px={3} py={3}>
                              <Text
                                fontSize="12px"
                                fontWeight="700"
                                color={COR.ink}
                                textTransform="uppercase"
                                letterSpacing="0.04em"
                              >
                                Total da composição
                              </Text>
                            </Td>
                            {/* Cai exatamente sob "Em aberto" — é a coluna que
                                soma. Somar sob "Valor original" daria um total
                                que não bate com a conciliação. */}
                            <Td px={3} py={3} textAlign="right">
                              <Text fontSize="13px" fontWeight="700" color={COR.ink} sx={NUM}>
                                {fmtMoney(data?.total_composicao)}
                              </Text>
                            </Td>
                            <Td colSpan={geo.resto} />
                          </Tr>
                        </>
                      )}
                    </Tbody>
                  </Table>
                </Box>

                {/* ── Paginação ──
                    Amostragem escolhida pelo usuário (25/50/100, padrão 25). A
                    conta 856/201 tem 22.095 itens: sem isto a tela mandava os
                    22 mil de uma vez (payload de ~7 MB). */}
                {paginacao?.paginado && paginacao.paginas > 1 && (
                  <Flex
                    mt={3}
                    align="center"
                    gap={2}
                    wrap="wrap"
                    justify="space-between"
                    borderTop="1px solid"
                    borderColor={COR.linha}
                    pt={3}
                  >
                    <Flex align="center" gap={2} wrap="wrap">
                      <Text fontSize="12.5px" color={COR.muted}>
                        Mostrando{' '}
                        <b>
                          {paginacao.offset + 1}–
                          {Math.min(paginacao.offset + porPagina, paginacao.total_filtrado)}
                        </b>{' '}
                        de <b>{paginacao.total_filtrado}</b>{' '}
                        {resumoFiltro?.ativo ? 'itens filtrados' : 'itens'}
                      </Text>
                      <Select
                        size="xs"
                        w="120px"
                        borderRadius="md"
                        bg="white"
                        value={porPagina}
                        onChange={e => trocarPorPagina(e.target.value)}
                        isDisabled={isFetching}
                        aria-label="Linhas por página"
                        title="Quantas linhas a grade mostra por página"
                      >
                        {OPCOES_ITENS_POR_PAGINA.map(o => (
                          <option key={o} value={o}>
                            {o} linhas
                          </option>
                        ))}
                      </Select>
                    </Flex>

                    <Flex align="center" gap={2}>
                      <Button
                        size="xs"
                        variant="outline"
                        borderRadius="md"
                        leftIcon={<CaretLeft size={12} weight="bold" />}
                        isDisabled={paginacao.pagina_atual <= 1 || isFetching}
                        onClick={() => irParaPagina(paginacao.pagina_atual - 1)}
                      >
                        Anterior
                      </Button>
                      <Text fontSize="12.5px" color={COR.ink} fontWeight="600" sx={NUM}>
                        Página {paginacao.pagina_atual} de {paginacao.paginas}
                      </Text>
                      <Button
                        size="xs"
                        variant="outline"
                        borderRadius="md"
                        rightIcon={<CaretRight size={12} weight="bold" />}
                        isDisabled={!paginacao.tem_proxima || isFetching}
                        onClick={() => irParaPagina(paginacao.pagina_atual + 1)}
                      >
                        Próxima
                      </Button>
                    </Flex>
                  </Flex>
                )}

                {/* ── A ALMA DA TELA: conciliação com o razão ── */}
                {data && (
                  <Flex
                    mt={3}
                    align="center"
                    gap={1.5}
                    wrap="wrap"
                    fontSize="13px"
                    borderTop="1px solid"
                    borderColor={COR.linha}
                    pt={3}
                  >
                    <Text fontWeight="700" color={COR.ink}>
                      Conciliação com o razão:
                    </Text>
                    <Text color={COR.muted}>razão em {fmtData(data.data_posicao)} =</Text>
                    <Text fontWeight="700" color={COR.ink} sx={NUM}>
                      {fmtSaldoComNatureza(data.saldo_razao, data.saldo_razao_natureza)}
                    </Text>
                    <Text color={COR.faint}>·</Text>
                    <Text color={COR.muted}>composição =</Text>
                    <Text fontWeight="700" color={COR.ink} sx={NUM}>
                      {fmtMoney(data.total_composicao)}
                    </Text>
                    <Text color={COR.faint}>·</Text>
                    <Text color={COR.muted}>diferença =</Text>
                    <Text
                      fontWeight="700"
                      color={selo.verde ? COR.ink : COR.warn}
                      sx={NUM}
                      ml={0.5}
                    >
                      {fmtMoney(data.diferenca)}
                    </Text>
                    <Badge
                      ml={1}
                      borderRadius="full"
                      px={2.5}
                      py={1}
                      fontSize="11px"
                      fontWeight="700"
                      textTransform="none"
                      bg={selo.verde ? 'rgba(22, 163, 74, 0.10)' : COR.warnBg}
                      color={selo.verde ? COR.pos : COR.warn}
                      title={selo.titulo}
                    >
                      {selo.texto}
                    </Badge>
                  </Flex>
                )}
              </>
            )}
          </GlassCard>
        )}
      </Box>

      {/* Painel de pagamentos do título. Fora do <Box> para o overlay cobrir
          a página inteira, e não só a área de conteúdo. */}
      <DrawerLigacoes
        item={itemLigacoes}
        lancamentos={ligados?.itens}
        carregando={carregandoLigados}
        aberto={!!itemLigacoes}
        onFechar={() => setItemLigacoes(null)}
        onIrAoRazao={irAoRazao}
      />
    </GlassPageBg>
  );
}

/** Chave estável da linha: o número do lançamento pode repetir entre as pernas. */
const chaveItem = (i: IComposicaoItem): string =>
  `${i.tipo}-${i.numero ?? 'sn'}-${i.data ?? ''}-${i.valor_aberto}-${i.documento ?? ''}`;

/**
 * Uma linha da composição, nas 13 colunas da planilha. Sem coluna D/C — a
 * natureza fica no header.
 *
 * Toda célula passa por `ouTraco`/`fmtCnpj`/`fmtDias`: campo que não veio
 * aparece como "—", nunca em branco e nunca como "null". Numa tabela onde 5
 * colunas só casam em 63% dos casos, o traço é o que diz ao contador "aqui não
 * achei a nota" em vez de deixá-lo achando que a tela perdeu o dado.
 */
function LinhaItem({
  item,
  destaque,
  colunas,
  onAbrirLigacoes,
}: {
  item: IComposicaoItem;
  destaque?: boolean;
  colunas: IColunaPapel[];
  onAbrirLigacoes?: (i: IComposicaoItem) => void;
}) {
  const parcial = item.parcial && item.valor_original > item.valor_aberto;
  const celula = { px: 3, py: 2.5, fontSize: '13px', color: COR.ink } as const;

  // Conteúdo POR TÍTULO de coluna. A linha renderiza exatamente as colunas que
  // o cabeçalho renderizou (as duas leem a mesma `colunas`), então célula e
  // cabeçalho não podem divergir — que é o defeito clássico de tabela com
  // coluna opcional: some o cabeçalho e os valores escorregam uma casa.
  const conteudo: Record<string, React.ReactNode> = {
    Data: (
      <Text sx={NUM} whiteSpace="nowrap">
        {fmtData(item.data)}
      </Text>
    ),
    'Lanç.': <Text sx={NUM}>{ouTraco(item.numero)}</Text>,
    'Nota fiscal': <Text sx={NUM}>{ouTraco(item.nota_numero)}</Text>,
    Série: <Text sx={NUM}>{ouTraco(item.nota_serie)}</Text>,
    'Data da nota': (
      <Text sx={NUM} whiteSpace="nowrap">
        {item.nota_data ? fmtData(item.nota_data) : '—'}
      </Text>
    ),
    // Nome inteiro no `title`: a coluna trunca, mas o contador confere o
    // fornecedor passando o mouse, sem precisar abrir o Excel.
    Fornecedor: (
      <Text noOfLines={1} title={item.fornecedor ?? undefined}>
        {ouTraco(item.fornecedor)}
      </Text>
    ),
    CNPJ: (
      <Text sx={NUM} whiteSpace="nowrap">
        {fmtCnpj(item.fornecedor_cnpj)}
      </Text>
    ),
    Filial: <Text sx={NUM}>{ouTraco(item.filial)}</Text>,
    // Duas linhas: "831 Despesa Servicos Prestados - PJ" não cabe numa só, e
    // em três a linha inteira da tabela dobra de altura.
    Contrapartida: (
      <Text noOfLines={2} title={item.contrapartida_label ?? undefined}>
        {ouTraco(item.contrapartida_label)}
      </Text>
    ),
    'Valor original': <Text sx={NUM}>{fmtMoney(item.valor_original)}</Text>,
    'Em aberto': (
      <>
        <Text fontSize="13px" fontWeight="700" color={destaque ? COR.warn : COR.ink} sx={NUM}>
          {fmtMoney(valorComSinal(item))}
        </Text>
        {parcial && (
          <Text fontSize="11px" color={COR.muted} sx={NUM}>
            baixa parcial
          </Text>
        )}
      </>
    ),
    Dias: <Text sx={NUM}>{fmtDias(item.dias)}</Text>,
    Histórico: (
      <>
        <Text noOfLines={1} title={item.historico ?? undefined}>
          {ouTraco(item.historico)}
        </Text>
        {/* Pagamento a maior é SINALIZADO, nunca reclassificado. */}
        {item.alerta && (
          <Text fontSize="11px" color={COR.warn} fontWeight="600" mt={0.5}>
            {item.alerta}
          </Text>
        )}
      </>
    ),
  };

  return (
    <Tr
      borderBottom="1px solid"
      borderColor={COR.linha}
      bg={destaque ? COR.warnBg : undefined}
      _hover={{ bg: destaque ? 'rgba(217, 119, 6, 0.12)' : 'rgba(15, 23, 42, 0.03)' }}
    >
      {/*
        A célula de ação fica FORA de `colunas` de propósito: a geometria
        (colSpan do rótulo e do resto, em `colunasVisiveis.geometria`) é
        derivada do índice de 'Em aberto', e uma coluna a mais na lista
        deslocaria a linha de TOTAL. Como ela é fixa e sempre a primeira,
        cabeçalho e rodapé a compensam com uma célula vazia.
      */}
      <Td {...celula} px={1} textAlign="center" width="34px">
        {temLigacao(item) ? (
          <Tooltip
            label={`${qtdLigacoes(item)} pagamento(s) ligado(s)`}
            fontSize="11px"
            placement="right"
            hasArrow
          >
            <IconButton
              aria-label="Ver pagamentos ligados"
              icon={<DotsThreeVertical size={16} weight="bold" />}
              size="xs"
              variant="ghost"
              color={COR.brand}
              onClick={() => onAbrirLigacoes?.(item)}
            />
          </Tooltip>
        ) : null}
      </Td>
      {colunas.map(c => (
        <Td key={c.titulo} {...celula} textAlign={c.alinha ?? 'left'}>
          {conteudo[c.titulo] ?? '—'}
        </Td>
      ))}
    </Tr>
  );
}
